import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageMetadata from '../hooks/usePageMetadata';
import { useAuth } from '../context/AuthContext';
import { authSection, btnLg, btnPrimary, containerForm, errorTextClass, focusRing, inputClass, labelClass } from '../styles/ui';

const LoginPage = () => {
  const { login, authStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/account';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  usePageMetadata({
    title: 'Sign In | Fresh & Homely',
    description: 'Sign in to your Fresh & Homely account to shop, check out and track your orders.',
    canonicalPath: '/login',
  });

  if (authStatus === 'authenticated') {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        navigate(`/verify-email?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <section className={authSection}>
        <div className={`${containerForm} w-full`}>
          <div className="bg-white/90 backdrop-blur-sm rounded-4xl border border-emerald-50 p-8 shadow-floating">
            <h1 className="serif text-3xl text-emerald-950 mb-3 text-center">Welcome back</h1>
            <div className="flex justify-center mb-8">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-4 py-1.5 rounded-full">
                Sign in to continue shopping
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className={labelClass}>Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="password" className={labelClass}>Password</label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="current-password"
                />
              </div>

              {error && <p className={errorTextClass}>{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className={`w-full ${btnPrimary} ${btnLg}`}
              >
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-ink-secondary space-y-2">
              <p>
                <Link to="/forgot-password" className={`text-emerald-700 hover:text-emerald-800 font-medium ${focusRing}`}>
                  Forgot your password?
                </Link>
              </p>
              <p>
                New here?{' '}
                <Link to="/register" className={`text-emerald-700 hover:text-emerald-800 font-medium ${focusRing}`}>
                  Create an account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default LoginPage;
